import { useEffect, useState } from "react";
import { api, errorText, useFetch } from "../api";
import { Alerts, Field, Loading, Page } from "../components/ui";
import { when } from "../format";

const EMPTY = {
  siteName: "",
  supportEmail: "",
  currency: "",
  directCommissionPercent: "",
  levelPercents: ["", "", "", "", ""],
  withdrawalFeePercent: "",
  minWithdrawal: "",
  maxWithdrawal: "",
};

export default function Settings() {
  const { data, loading, error, reload } = useFetch("/settings");
  const saved = data?.data;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!saved) return;
    const levels = Array.isArray(saved.levelPercents) ? saved.levelPercents.slice(0, 5) : [];
    while (levels.length < 5) levels.push(0);
    setForm({
      siteName: saved.siteName || "",
      supportEmail: saved.supportEmail || "",
      currency: saved.currency || "",
      directCommissionPercent: saved.directCommissionPercent ?? "",
      levelPercents: levels,
      withdrawalFeePercent: saved.withdrawalFeePercent ?? "",
      minWithdrawal: saved.minWithdrawal ?? "",
      maxWithdrawal: saved.maxWithdrawal ?? "",
    });
  }, [saved]);

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function setLevel(index, value) {
    setForm((current) => {
      const levelPercents = current.levelPercents.slice();
      levelPercents[index] = value;
      return { ...current, levelPercents };
    });
  }

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    setFormError("");
    setErrors({});
    try {
      await api("/settings", {
        method: "PATCH",
        body: {
          siteName: form.siteName.trim(),
          supportEmail: form.supportEmail.trim(),
          currency: form.currency.trim(),
          directCommissionPercent: Number(form.directCommissionPercent),
          levelPercents: form.levelPercents.map((item) => Number(item)),
          withdrawalFeePercent: Number(form.withdrawalFeePercent),
          minWithdrawal: Number(form.minWithdrawal),
          maxWithdrawal: Number(form.maxWithdrawal),
        },
      });
      setNotice("Settings saved");
      reload();
    } catch (err) {
      setFormError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page title="Settings" text="These values apply to the next purchase, the transfer screen and new withdrawals. Direct commission is paid once, on a direct referral's first package. Level percents apply to the next nightly ROI. Old purchases keep their own rate.">
      <Alerts error={error || formError} notice={notice} />
      {loading && !saved ? <Loading /> : null}
      {saved ? (
        <form className="panel" onSubmit={onSubmit}>
          <div className="form-grid">
            <Field label="Site name" error={errors.siteName}>
              <input maxLength={60} value={form.siteName} onChange={(event) => set("siteName", event.target.value)} />
            </Field>
            <Field label="Support email" error={errors.supportEmail}>
              <input type="email" value={form.supportEmail} onChange={(event) => set("supportEmail", event.target.value)} />
            </Field>
            <Field label="Currency" error={errors.currency} hint="2 to 10 letters. Match a BEP20 coin short name if package purchases should spend that coin.">
              <input maxLength={10} value={form.currency} onChange={(event) => set("currency", event.target.value.toUpperCase())} />
            </Field>
            <Field label="Direct commission %" error={errors.directCommissionPercent} hint="Percent of a direct referral's first package. 5 on a 100 package pays 5 once, into the sponsor wallet immediately. That referral's next package does not pay it again.">
              <input type="number" min="0" max="100" step="0.01" value={form.directCommissionPercent} onChange={(event) => set("directCommissionPercent", event.target.value)} />
            </Field>
            <Field label="Withdrawal fee %" error={errors.withdrawalFeePercent} hint="Stored on the next request. The fee is not sent. Approve sends the amount after this percent.">
              <input type="number" min="0" max="100" step="0.01" value={form.withdrawalFeePercent} onChange={(event) => set("withdrawalFeePercent", event.target.value)} />
            </Field>
            <Field label="Minimum withdrawal" error={errors.minWithdrawal} hint="Requested amount">
              <input type="number" min="0" step="0.01" value={form.minWithdrawal} onChange={(event) => set("minWithdrawal", event.target.value)} />
            </Field>
            <Field label="Maximum withdrawal" error={errors.maxWithdrawal} hint="Requested amount, at least the minimum">
              <input type="number" min="0" step="0.01" value={form.maxWithdrawal} onChange={(event) => set("maxWithdrawal", event.target.value)} />
            </Field>
          </div>
          <h2 className="section-title">Level percents</h2>
          <p className="hint">Level 1 is the direct sponsor. Level 5 is the fifth upline. Each number is a percent of that downline member's daily ROI. Level 1 pays after 1 direct package buyer, Level 2 after 2, up to Level 5. A blocked sponsor is skipped and the next sponsor keeps their own level.</p>
          <div className="form-grid">
            {form.levelPercents.map((value, index) => (
              <Field key={index} label={"Level " + (index + 1)} error={index === 0 ? errors.levelPercents : ""}>
                <input type="number" min="0" max="100" step="0.01" value={value} onChange={(event) => setLevel(index, event.target.value)} />
              </Field>
            ))}
          </div>
          <p className="hint">Last saved {when(saved.updatedAt)}</p>
          <div className="form-actions">
            <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save settings"}</button>
          </div>
        </form>
      ) : null}
    </Page>
  );
}
